import { Component, ElementRef, HostBinding, OnInit } from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { CMSEvent } from 'render/models/cms-event.model';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { Observable } from 'rxjs';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';
import { toNumber } from 'shared/utils/data-utils';
import { hasTouchScreen } from 'shared/utils/feature-detection';

import { LinkedProduct, PieceShowcaseSettings, TooltipPosition } from './models/piece-showcase.models';
import { templateUrl, styleUrls } from './piece-showcase-render.component.paths';


@Component({
  selector: 'piece-showcase-render',
  templateUrl,
  styleUrls
})
export class PieceShowcaseRenderComponent
  extends BaseRenderComponent<PieceShowcaseSettings>
  implements OnInit {

  public orientation$!: Observable<string>;
  public linkedProducts$?: Observable<Product[]>;

  constructor(
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly productsService: ProductsService,
    private readonly renderEventsService: RenderEventsService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly elRef: ElementRef
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$;
  }

  @HostBinding('style.--imageObjectFit')
  public get imageObjectFit(): string | undefined {
    if (this.piece?.settings?.styles.objectFit) {
      return this.piece.settings.styles.objectFit;
    }
    return undefined;
  }

  @HostBinding('style.--imageObjectPosition')
  public get imageObjectPosition(): string | undefined {
    if (this.piece?.settings?.styles.objectPosition) {
      return this.piece.settings.styles.objectPosition;
    }
    return undefined;
  }

  @HostBinding('style.--markerIconWidth')
  public get markerIconWidth(): string | undefined {
    if (this.renderByDeviceService.isMobile) {
      return this.piece?.settings?.general.markerIconSize.widthMobile;
    }

    return this.piece?.settings?.general.markerIconSize.widthDesktop;
  }

  @HostBinding('style.--markerIconHeight')
  public get markerIconHeight(): string | undefined {
    if (this.renderByDeviceService.isMobile) {
      return this.piece?.settings?.general.markerIconSize.heightMobile;
    }

    return this.piece?.settings?.general.markerIconSize.heightDesktop;
  }

  @HostBinding('style.--alwaysVisibleTooltipsOpacity')
  public get alwaysVisibleTooltipsOpacity(): number {
    return this.hasToAlwaysDisplayTooltips ? 1 : 0;
  }

  @HostBinding('style.--alwaysVisibleTooltipsVisibility')
  public get alwaysVisibleTooltipsVisibility(): string {
    return this.hasToAlwaysDisplayTooltips ? 'visible' : 'hidden';
  }

  public get hasToAlwaysDisplayTooltips(): boolean {
    return !!this.settings.general?.alwaysVisibleTooltips;
  }

  public get customMarkerIcon(): string | undefined {
    const iconUrl = this.settings.general.customMarkerIcon?.image;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return iconUrl ? this.assetsSharedService.getAssetUrl(iconUrl, assetsUrl, akamaiOptions) : undefined;
  }

  public get showTooltips(): boolean {
    return !!this.settings.general?.showTooltipsToggle && !hasTouchScreen();
  }

  public ngOnInit(): void {
    const linkedProductIds = this.piece?.settings?.general.linkedProducts.map((lp) => lp.product)
      .filter((elem, index, self) => index === self.indexOf(elem));
    if (linkedProductIds && linkedProductIds.length > 0) {
      this.linkedProducts$ = this.productsService.productsDetailArray(linkedProductIds.join(','));
    }
  }

  public getPortraitImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.portraitImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.elRef),
      impolicy: this.settings.general.impolicy
    };

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      height: toNumber(this.settings.general.portraitImage?.height),
      width: toNumber(this.settings.general.portraitImage?.width)
    };
  }

  public getLandscapeImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.landscapeImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.elRef),
      impolicy: this.settings.general.impolicy
    };

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      width: toNumber(this.settings.general.landscapeImage?.width),
      height: toNumber(this.settings.general.landscapeImage?.height)
    };
  }

  public getProductLink(product: LinkedProduct): Link {
    return {
      type: 'product',
      pieceName: this.piece?.name,
      identifier: product.identifier,
      productId: product.product,
      productPartNumberDetails: {
        colorId: product.productLinkDetails?.colorId,
        style: product.productLinkDetails?.style
      },
      enableUnifiedUrl: product.enableUnifiedUrl
    };
  }

  public onProductClick(product: LinkedProduct, products: Product[]): void {
    const event = new Event('productClicked');
    const renderEvent: RenderEvent = {
      event,
      extra: {
        type: 'showcaseProductClicked',
        identifier: product.identifier,
        productId: product.product,
        productPartNumberDetails: {
          colorId: product.productLinkDetails?.colorId,
          style: product.productLinkDetails?.style
        },
        enableUnifiedUrl: product.enableUnifiedUrl
      },
      location: null
    };
    this.renderEventsService.events.next(renderEvent);

    const productData = products.find((p) => p.id === product.product) as Product;
    const cmsEvent: CMSEvent<Product> = {
      name: 'showcaseProductClicked',
      type: 'click',
      identifier: product.identifier,
      data: {
        ...productData
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public getTooltipClass(tooltipPos: TooltipPosition = 'up'): string {
    return `tooltip tooltip-${tooltipPos}`;
  }

}
